import { useCallback, useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useAbortController from "@common/hooks/useAbortController";
import { IssueAI } from "@common/models/issueTracking/IssueAI.model";
import { IssueSummaryUpdatedEvent, SocketEvents } from "@common/models/webSocketEvents";
import { useAppContext } from "@main/context/App/App.context";
import { useUserFeedbackDeps } from "@main/pages/UserFeedback/UserFeedback";

interface Props {
  selectedWorkspaceId?: string;
  selectedAppId?: string;
  userFeedbackId?: string;
  activityRefresh?: boolean;
  setRefreshActivity?: (refreshActivity: boolean) => void;
  enabled: boolean;
}

export default function useUserFeedbackAiSummaryConsumer({
  selectedWorkspaceId,
  selectedAppId,
  userFeedbackId,
  enabled,
}: Props) {
  const [response, setResponse] = useState<IssueAI>();
  const { userFeedbackService } = useUserFeedbackDeps();
  const { abortSignal } = useAbortController();
  const { socket } = useAppContext();

  const getUserFeedbackSummary = useCallback(() => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackId) return undefined;
    userFeedbackService
      .getUserFeedbackSummary(selectedWorkspaceId, selectedAppId, userFeedbackId)
      .then(({ data }) => {
        setResponse(data);
        return data as IssueAI;
      })
      .catch(() => {
        if (abortSignal.aborted) return;
        displayToast({ title: "Something went wrong", content: "Please try again." });
      });
  }, [abortSignal.aborted, selectedAppId, selectedWorkspaceId, userFeedbackId, userFeedbackService]);

  const generateSummary = async () => {
    if (!selectedWorkspaceId || !selectedAppId || !userFeedbackId) return;

    try {
      return userFeedbackService
        .generateUserFeedbackSummary(selectedWorkspaceId, selectedAppId, userFeedbackId)
        .then(({ data }) => {
          setResponse(data);
          return data as IssueAI;
        });
    } catch (error) {
      if (abortSignal.aborted) return undefined;
      return undefined;
    }
  };

  useEffect(() => {
    if (!enabled) return;

    getUserFeedbackSummary();

    const eventHandler = (event: IssueSummaryUpdatedEvent) => {
      if (userFeedbackId === event.issue_id) getUserFeedbackSummary();
    };
    socket?.on(SocketEvents.ISSUE_SUMMARY_UPDATED, eventHandler);

    return () => {
      socket?.off(SocketEvents.ISSUE_SUMMARY_UPDATED, eventHandler);
    };
  }, [enabled, getUserFeedbackSummary, socket, userFeedbackId]);

  return { response, generateSummary };
}
